import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { Calculator, Save, Download, Building2, FilePlus2, Plus, Trash2 } from "lucide-react";
import { ContractShiftsEditor } from "@/components/ContractShiftsEditor";
import { newEmptyShift } from "@/lib/contractShifts";
import { formatARS } from "@/lib/money";
import { MoneyInput } from "@/components/MoneyInput";

const RATE_TYPE_LABEL = { normal: "Normal", nocturno: "Nocturno", fin_de_semana: "Fin de semana" };
const RATE_TYPE_BADGE = { normal: "gray", nocturno: "blue", fin_de_semana: "purple" };
const money = (n) => formatARS(n); // Fix BR: $ 1.234.567,89
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-AR") : "—";

const COST_FIELDS = [
  { k: "cost_sueldo", label: "Sueldo del vigilador ($/mes)", testid: "budget-cost-sueldo" },
  { k: "cost_equipamiento", label: "Equipamiento ($/mes)", testid: "budget-cost-equipamiento" },
  { k: "cost_uniforme", label: "Uniforme ($/mes)", testid: "budget-cost-uniforme" },
  { k: "cost_software", label: "Software ($/mes)", testid: "budget-cost-software" },
  { k: "cost_sac1", label: "SAC 1 ($/mes)", testid: "budget-cost-sac1" },
  { k: "cost_sac2", label: "SAC 2 ($/mes)", testid: "budget-cost-sac2" },
];
const EMPTY_COSTS = { cost_sueldo: 0, cost_equipamiento: 0, cost_uniforme: 0, cost_software: 0, cost_sac1: 0, cost_sac2: 0, monthly_hours_base: 200, extra_costs: [] };
// Fix BR (2026-11-03): SAC automático = sueldo ÷ 24 en SAC 1 y en SAC 2 (un sueldo por año ÷ 12 meses, en dos partes).
const sacFromSueldo = (s) => Math.round(((Number(s) || 0) / 24) * 100) / 100;

export function SimulateTab({ token }) {
  const headers = { "X-Budget-Pin-Token": token };
  const [clients, setClients] = useState([]);
  const [mode, setMode] = useState("existing"); // existing | new
  const [clientId, setClientId] = useState("");
  const [objectiveName, setObjectiveName] = useState("");
  const [shifts, setShifts] = useState([newEmptyShift()]);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [holidayHours, setHolidayHours] = useState("");
  const [costs, setCosts] = useState(EMPTY_COSTS);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [savedNumber, setSavedNumber] = useState(null);
  const [savedValidUntil, setSavedValidUntil] = useState(null);
  const [sacAuto, setSacAuto] = useState(true); // Fix BR
  const [validityDays, setValidityDays] = useState(30); // Fix BR: "válido por X días"
  const [includesText, setIncludesText] = useState(""); // Fix BR: "El servicio incluye", uno por renglón

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/clients"); setClients(data); }
    catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/budget/settings", { headers });
      if (data.last_costs) {
        setCosts((c) => ({ ...c, ...data.last_costs, extra_costs: data.last_costs.extra_costs || [] }));
        const calc = sacFromSueldo(data.last_costs.cost_sueldo);
        const manual = Math.abs((Number(data.last_costs.cost_sac1) || 0) - calc) > 0.01 || Math.abs((Number(data.last_costs.cost_sac2) || 0) - calc) > 0.01;
        if (manual && (Number(data.last_costs.cost_sac1) || Number(data.last_costs.cost_sac2))) setSacAuto(false);
      }
      if (data.default_client_includes) setIncludesText(data.default_client_includes.join("\n"));
      if (data.default_validity_days) setValidityDays(data.default_validity_days);
    } catch { /* no bloquea la simulación */ }
  })(); }, []); // eslint-disable-line

  const selectedClient = clients.find((c) => c.id === clientId);
  const setCost = (k, v) => setCosts((c) => ({ ...c, [k]: v }));
  const sacCalc = sacFromSueldo(costs.cost_sueldo);
  useEffect(() => {
    if (sacAuto) setCosts((c) => (c.cost_sac1 === sacCalc && c.cost_sac2 === sacCalc ? c : { ...c, cost_sac1: sacCalc, cost_sac2: sacCalc }));
  }, [sacAuto, sacCalc]);
  const setExtra = (i, k, v) => setCosts((c) => ({ ...c, extra_costs: (c.extra_costs || []).map((e, idx) => (idx === i ? { ...e, [k]: v } : e)) }));
  const addExtra = () => setCosts((c) => ({ ...c, extra_costs: [...(c.extra_costs || []), { label: "", amount: 0 }] }));
  const removeExtra = (i) => setCosts((c) => ({ ...c, extra_costs: (c.extra_costs || []).filter((_, idx) => idx !== i) }));
  const extrasTotal = (costs.extra_costs || []).reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const estimatedMonthlyCost = COST_FIELDS.reduce((sum, f) => sum + (Number(costs[f.k]) || 0), 0) + extrasTotal;
  const estimatedHourlyRate = costs.monthly_hours_base > 0 ? estimatedMonthlyCost / Number(costs.monthly_hours_base) : 0;

  const costsPayload = () => ({
    cost_sueldo: Number(costs.cost_sueldo) || 0, cost_equipamiento: Number(costs.cost_equipamiento) || 0,
    cost_uniforme: Number(costs.cost_uniforme) || 0, cost_software: Number(costs.cost_software) || 0,
    cost_sac1: Number(costs.cost_sac1) || 0, cost_sac2: Number(costs.cost_sac2) || 0,
    monthly_hours_base: Number(costs.monthly_hours_base) || 200,
    extra_costs: (costs.extra_costs || []).filter((e) => Number(e.amount) > 0).map((e) => ({ label: e.label || "", amount: Number(e.amount) || 0 })),
  });

  const calculate = async () => {
    setBusy(true); setResult(null); setSavedId(null);
    try {
      const payload = mode === "existing"
        ? { client_id: clientId, year, month, holiday_hours: Number(holidayHours) || 0, ...costsPayload() }
        : { objective_name: objectiveName || "Objetivo nuevo", shifts, year, month, holiday_hours: Number(holidayHours) || 0, ...costsPayload() };
      const { data } = await api.post("/budget/calculate", payload, { headers });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!saveName.trim()) return toast.error("Ponele un nombre al presupuesto");
    setSaving(true);
    try {
      const payload = mode === "existing"
        ? { client_id: clientId, year, month, holiday_hours: Number(holidayHours) || 0, name: saveName, ...costsPayload() }
        : { objective_name: objectiveName || "Objetivo nuevo", shifts, year, month, holiday_hours: Number(holidayHours) || 0, name: saveName, ...costsPayload() };
      payload.validity_days = Math.min(365, Math.max(1, Number(validityDays) || 30)); // Fix BR
      payload.client_includes = includesText.split("\n").map((x) => x.trim()).filter(Boolean);
      const { data } = await api.post("/budget/save", payload, { headers });
      toast.success("Presupuesto guardado");
      setSavedId(data.id); setSavedNumber(data.number); setSavedValidUntil(data.valid_until);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/budget/${id}/print.pdf`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `presupuesto_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="budget-simulate-form">
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <ModeBtn active={mode === "existing"} onClick={() => setMode("existing")} Ic={Building2} label="Objetivo existente" testid="budget-mode-existing"/>
          <ModeBtn active={mode === "new"} onClick={() => setMode("new")} Ic={FilePlus2} label="Objetivo nuevo" testid="budget-mode-new"/>
        </div>

        {mode === "existing" ? (
          <div className="field"><label>Cliente / Objetivo *</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="budget-client-select">
              <option value="">Elegir objetivo...</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {selectedClient && (
              (selectedClient.contract_shifts || []).length === 0 ? (
                <p style={{ fontSize: 11, color: "var(--invict-red)", marginTop: 6 }}>Este objetivo no tiene franjas horarias cargadas todavía (Ficha del cliente → Datos del servicio contratado).</p>
              ) : (
                <div style={{ marginTop: 10 }}>
                  <ContractShiftsEditor shifts={selectedClient.contract_shifts} onChange={() => {}} readOnly testIdPrefix="budget-existing-shift"/>
                </div>
              )
            )}
          </div>
        ) : (
          <>
            <div className="field"><label>Nombre del objetivo (referencia)</label>
              <input value={objectiveName} onChange={(e) => setObjectiveName(e.target.value)} placeholder="ej. Barrio Los Álamos (a cotizar)" data-testid="budget-objective-name"/>
            </div>
            <ContractShiftsEditor shifts={shifts} onChange={setShifts} testIdPrefix="budget-shift"/>
          </>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginTop: 14 }}>
          <div className="field"><label>Mes a simular</label>
            <input type="month" value={`${year}-${String(month).padStart(2, "0")}`}
              onChange={(e) => { const [y, m] = e.target.value.split("-"); setYear(Number(y)); setMonth(Number(m)); }}
              data-testid="budget-month"/>
          </div>
          <div className="field"><label>Horas en feriados (estimado, opcional)</label>
            <input type="number" min="0" step="0.5" value={holidayHours} onChange={(e) => setHolidayHours(e.target.value)} placeholder="0" data-testid="budget-holiday-hours"/>
          </div>
        </div>

        <h4 style={{ fontSize: 13, margin: "18px 0 4px", color: "#0A0A0A" }}>Costos de este presupuesto</h4>
        <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 10px" }}>
          Cada presupuesto tiene sus propios costos (no son valores fijos). Se precargan los últimos usados, pero siempre son editables.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
          {COST_FIELDS.map((f) => {
            const isSac = f.k === "cost_sac1" || f.k === "cost_sac2";
            return (
              <div className="field" key={f.k}><label>{f.label}{isSac && sacAuto ? " · automático" : ""}</label>
                <MoneyInput value={costs[f.k]} onChange={(v) => setCost(f.k, v)} disabled={isSac && sacAuto} testId={f.testid}/>
              </div>
            );
          })}
          <div className="field"><label>Horas base mensuales (para valor hora)</label>
            <input type="number" min="1" step="1" value={costs.monthly_hours_base} onChange={(e) => setCost("monthly_hours_base", e.target.value)} data-testid="budget-monthly-hours-base"/>
          </div>
        </div>
        <div style={{ marginTop: 8, padding: "8px 12px", background: "var(--invict-bone-2)", fontSize: 12 }} data-testid="budget-sac-auto">
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, cursor: "pointer" }}>
            <input type="checkbox" checked={sacAuto} onChange={(e) => setSacAuto(e.target.checked)} data-testid="budget-sac-auto-toggle"/>
            Calcular el SAC automáticamente desde el sueldo
          </label>
          <div style={{ marginTop: 4, color: "var(--invict-text-muted)" }} data-testid="budget-sac-explain">
            El SAC (aguinaldo) es medio sueldo por semestre: un sueldo por año. Por mes se previsiona sueldo ÷ 12, repartido entre SAC 1 (junio) y SAC 2 (diciembre):
            {" "}{money(costs.cost_sueldo)} ÷ 24 = <b>{money(sacCalc)}</b> en cada uno.{" "}
            {sacAuto ? "Destildá la opción para cargarlo a mano." : "Ahora se carga a mano."}
          </div>
        </div>
        <div style={{ marginTop: 10 }} data-testid="budget-extra-costs">
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>Otros costos ($/mes por puesto) — se suman al valor de la hora</div>
          {(costs.extra_costs || []).map((e, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              <input value={e.label} onChange={(ev) => setExtra(i, "label", ev.target.value)} placeholder="ej. Vacaciones, reemplazos, ART, habilitación DISEP" style={{ flex: 2 }} data-testid={`budget-extra-label-${i}`}/>
              <div style={{ flex: 1 }}><MoneyInput value={e.amount} onChange={(v) => setExtra(i, "amount", v)} testId={`budget-extra-amount-${i}`}/></div>
              <button type="button" onClick={() => removeExtra(i)} className="btn-ghost" style={{ padding: "6px 10px" }} data-testid={`budget-extra-remove-${i}`}><Trash2 size={13}/></button>
            </div>
          ))}
          <button type="button" onClick={addExtra} className="btn-ghost" style={{ padding: "6px 12px", fontSize: 11 }} data-testid="budget-extra-add"><Plus size={12} style={{ marginRight: 4 }}/>Agregar costo</button>
        </div>
        <div style={{ marginTop: 8, padding: "8px 12px", background: "#FFFBF4", borderLeft: "3px solid #BF3437", fontSize: 12 }} data-testid="budget-estimated-hourly-rate">
          Valor de la hora base: <b>{money(estimatedHourlyRate)}</b> (suma de todos los costos ÷ horas base). El valor de la hora al cliente suma además cargas sociales, gastos, supervisión, adicionales y margen: se ve al presionar "Calcular presupuesto".
        </div>

        <button type="button" onClick={calculate} disabled={busy || (mode === "existing" && !clientId)} className="btn-primary" style={{ width: "100%", padding: 12, marginTop: 14 }} data-testid="budget-calculate-btn">
          <Calculator size={14} style={{ display: "inline", marginRight: 6 }}/>{busy ? "Calculando..." : "Calcular presupuesto"}
        </button>
      </div>

      {result && (
        <div className="card" style={{ padding: 18 }} data-testid="budget-result">
          <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>{result.objective_label} — {String(result.month).padStart(2, "0")}/{result.year}</h3>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 16 }}>
            <thead><tr style={{ background: "#0A0A0A", color: "#FFFBF4" }}>
              <th style={{ padding: 7, textAlign: "left" }}>Turno</th><th style={{ padding: 7, textAlign: "left" }}>Horario</th>
              <th style={{ padding: 7 }}>Personal</th><th style={{ padding: 7 }}>Tipo</th><th style={{ padding: 7 }}>Horas/mes</th>
              <th style={{ padding: 7 }}>Tarifa/h</th><th style={{ padding: 7 }}>Subtotal</th>
            </tr></thead>
            <tbody>
              {result.shifts.map((s, i) => (
                <tr key={s.id || i} style={{ borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`budget-shift-row-${i}`}>
                  <td style={{ padding: 7 }}>{s.label}</td>
                  <td style={{ padding: 7 }}>{s.time_from}–{s.time_to}</td>
                  <td style={{ padding: 7, textAlign: "center" }}>{s.staff_count}</td>
                  <td style={{ padding: 7, textAlign: "center" }}><span className={`badge ${RATE_TYPE_BADGE[s.rate_type]}`}>{RATE_TYPE_LABEL[s.rate_type]}</span></td>
                  <td style={{ padding: 7, textAlign: "center" }}>{s.total_hours_month} h</td>
                  <td style={{ padding: 7, textAlign: "right" }}>{money(s.hourly_rate_applied)}</td>
                  <td style={{ padding: 7, textAlign: "right", fontWeight: 800 }}>{money(s.subtotal)}</td>
                </tr>
              ))}
              {result.holiday_hours > 0 && (
                <tr style={{ borderBottom: "1px solid var(--invict-bone-3)" }} data-testid="budget-holiday-row">
                  <td colSpan={4} style={{ padding: 7 }}><b>Horas en feriados (estimado)</b></td>
                  <td style={{ padding: 7, textAlign: "center" }}>{result.holiday_hours} h</td>
                  <td style={{ padding: 7, textAlign: "right" }}>{money(result.holiday_rate_applied)}</td>
                  <td style={{ padding: 7, textAlign: "right", fontWeight: 800 }}>{money(result.holiday_subtotal)}</td>
                </tr>
              )}
            </tbody>
          </table>

          <div style={{ maxWidth: 340, marginLeft: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 900, background: "#FFFBF4", padding: "6px 8px", borderLeft: "3px solid #BF3437" }} data-testid="budget-client-hourly-rate-result">
              <span>Valor de la hora al cliente (todo incluido, sin IVA)</span><span>{money(result.client_hourly_rate)}</span>
            </div>
            <Row label="Valor de la hora base (costos ÷ horas base)" value={money(result.hourly_rate)}/>
            <Row label="Subtotal mano de obra" value={money(result.subtotal_mano_obra)}/>
            <Row label={`Cargas sociales (${result.social_charges_pct}%)`} value={money(result.social_charges_amount)}/>
            <Row label="Costo de personal" value={money(result.costo_personal)}/>
            <Row label={`Gastos administrativos (${result.admin_expenses_pct}%)`} value={money(result.admin_expenses_amount)}/>
            <Row label={`Supervisión (${result.supervision_pct}%)`} value={money(result.supervision_amount)}/>
            <Row label="Costo total" value={money(result.costo_total)}/>
            <Row label={`Margen (${result.margin_pct}%)`} value={money(result.margin_amount)}/>
            <div style={{ borderTop: "1px solid var(--invict-bone-3)", paddingTop: 6, marginTop: 2, display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 800 }}>
              <span>Total sin IVA</span><span data-testid="budget-total-sin-iva">{money(result.total_sin_iva)}</span>
            </div>
            <Row label={`IVA (${result.iva_pct}%)`} value={money(result.iva_amount)}/>
            <div style={{ borderTop: "2px solid #0A0A0A", paddingTop: 8, marginTop: 4, display: "flex", justifyContent: "space-between", fontSize: 18, fontWeight: 900 }}>
              <span>PRECIO FINAL (con IVA)</span><span data-testid="budget-total-final">{money(result.precio_final_con_iva)}</span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 18, alignItems: "flex-end", flexWrap: "wrap" }}>
            {!savedId ? (
              <>
                <div className="field" style={{ margin: 0, flex: "1 1 220px" }}><label>Nombre para guardar</label>
                  <input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="ej. Presupuesto Barrio Los Álamos" data-testid="budget-save-name-input"/>
                </div>
                <div className="field" style={{ margin: 0, flex: "0 1 140px" }}><label>Válido por (días)</label>
                  <input type="number" min="1" max="365" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} data-testid="budget-validity-days"/>
                </div>
                <div className="field" style={{ margin: 0, flex: "1 1 100%" }}><label>El servicio incluye (para el presupuesto del cliente, uno por renglón, sin montos)</label>
                  <textarea rows={6} value={includesText} onChange={(e) => setIncludesText(e.target.value)} data-testid="budget-client-includes"/>
                </div>
                <button type="button" onClick={save} disabled={saving} className="btn-ghost" style={{ padding: "11px 16px" }} data-testid="budget-save-btn">
                  <Save size={14} style={{ display: "inline", marginRight: 6 }}/>{saving ? "Guardando..." : "Guardar presupuesto"}
                </button>
              </>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
                <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: 0 }} data-testid="budget-saved-meta">
                  Presupuesto Nº <b>{savedNumber}</b> · válido hasta <b>{fmtDate(savedValidUntil)}</b> ({validityDays} días). La versión para enviar al cliente está en la pestaña "Cliente".
                </p>
                <button type="button" onClick={() => downloadPdf(savedId)} className="btn-primary" style={{ padding: "11px 16px", alignSelf: "flex-start" }} data-testid={`budget-download-pdf-${savedId}`}>
                  <Download size={14} style={{ display: "inline", marginRight: 6 }}/>Descargar PDF interno
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ModeBtn({ active, onClick, Ic, label, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px", borderRadius: 8,
        border: `2px solid ${active ? "#BF3437" : "var(--invict-bone-3)"}`, background: active ? "#BF3437" : "#fff",
        color: active ? "#fff" : "#0A0A0A", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
      <Ic size={14}/> {label}
    </button>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
      <span style={{ color: "var(--invict-text-muted)" }}>{label}</span>
      <span style={{ fontWeight: 700, color: "#0A0A0A" }}>{value}</span>
    </div>
  );
}
